import { Form, Select } from "antd";
import * as React from "react";
import { t } from "i18next";
import { emptyValueMarker } from "src/common/emptyValueMarker";
import { filterOptionByName } from "src/common/filterOptionByName";
import { SelectFromReference } from "src/components/SelectFromReference";
import { ZObjectItem } from "src/types/ZObjectItem";
import { ZUid } from "src/types/zUid";
import { AttrTypeName } from "src/types/AttrType";
import { AttrTypeMap } from "src/common/attributes";
import {
  loadFilterSupportedAtts,
  loadFromAttr,
  loadObjectAttrinbutesAll,
} from "../objectsApi";

const loadFilterSupportedDictionaryAtts = async (
  objectId: ZUid,
  attrTypeMap: AttrTypeMap,
) => {
  const [allAtts, supportedAtts] = await Promise.all([
    loadObjectAttrinbutesAll(objectId, { translate: true }),
    loadFilterSupportedAtts(objectId),
  ]);
  const supportedAttrIds = new Set(supportedAtts.map(({ id }) => id));
  const filtered = allAtts.filter(
    ({ id, valueType }) =>
      supportedAttrIds.has(id) &&
      String(attrTypeMap[valueType]) === AttrTypeName.dictSingle,
  );
  return filtered;
};

interface PropsMenuLinkObjectFields {
  objectData: ZObjectItem[];
  attrTypeMap: AttrTypeMap;
}

export const MenuLinkObjectFields: React.FC<PropsMenuLinkObjectFields> = ({
  objectData,
  attrTypeMap,
}) => {
  const curObjectId = Form.useWatch(["link", "objectId"]);
  const curFilterAttrId = Form.useWatch([
    "link",
    "filterRestriction",
    "filterAttrId",
  ]);
  const form = Form.useFormInstance();

  const loadFilterAttrs = React.useCallback(async () => {
    if (!curObjectId) return [];
    const atts = await loadFilterSupportedDictionaryAtts(
      curObjectId,
      attrTypeMap,
    );
    return atts.map(({ id, name }) => ({ value: id, label: name }));
  }, [curObjectId]);

  const loadFilterAttrValues = React.useCallback(async () => {
    if (!curFilterAttrId) return [];
    const dictValues = await loadFromAttr(curFilterAttrId);
    return [
      ...dictValues.map(({ name }) => ({ value: name, label: name })),
      { value: emptyValueMarker, label: t(emptyValueMarker) },
    ];
  }, [curFilterAttrId]);

  const handleObjectIdChange = () => {
    form.setFieldValue(
      ["link", "filterRestriction", "filterAttrId"],
      undefined,
    );
    form.setFieldValue(
      ["link", "filterRestriction", "filterValueStr"],
      undefined,
    );
  };

  const handleFilterAttrIdChange = () => {
    form.setFieldValue(
      ["link", "filterRestriction", "filterValueStr"],
      undefined,
    );
  };
  return (
    <>
      <Form.Item name={["link", "objectId"]} rules={[{ required: true }]}>
        <Select
          allowClear
          onChange={handleObjectIdChange}
          options={objectData.map(({ name, id }) => ({
            value: id,
            label: name,
          }))}
          showSearch
          filterOption={filterOptionByName}
        />
      </Form.Item>

      <Form.Item
        name={["link", "filterRestriction", "filterAttrId"]}
        label="Атрибут ограничения списка"
      >
        <SelectFromReference
          allowClear
          showSearch
          onChange={handleFilterAttrIdChange}
          filterOption={filterOptionByName}
          loader={loadFilterAttrs}
        />
      </Form.Item>

      <Form.Item
        name={["link", "filterRestriction", "filterValueStr"]}
        label="Значение атрибута ограничения списка"
        rules={[{ required: Boolean(curFilterAttrId) }]}
      >
        <SelectFromReference
          allowClear
          showSearch
          disabled={!curFilterAttrId}
          filterOption={filterOptionByName}
          loader={loadFilterAttrValues}
        />
      </Form.Item>
    </>
  );
};
